import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  IconButton,
  Button,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  StrikethroughS,
  FormatListBulleted,
  FormatListNumbered,
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
  FormatAlignJustify,
  InsertLink,
  AttachFile,
  KeyboardVoice,
  Send,
  CloseOutlined,
} from "@mui/icons-material";

import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Placeholder from "@tiptap/extension-placeholder";
import TextAlign from "@tiptap/extension-text-align";
import Link from "@tiptap/extension-link";
import SpeechToTextPopup from "../../common/SpeechToTextPopup";
import CcBccField, { RecipientOption } from "./CcBccField";
import { useModalStyles } from "../../common/useModalStyles";
import { setSingleAlertObj, SingleAlertInfo } from "../../../redux/slice/commonSlice";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";

export type Attachment = {
  tempId: string,
  documentName: string;
  content: string;
  fileType: string;
  type: string
};

export default function ReplyBox({
  onCancel,
  onSend,
  onMicClick,
  onAttachFiles,
  onSent,
  placeholder = "Type your message…",
  accept,
  userOptions = [],
  toUserId,
  ccIds = [],
  bccIds = [],
  onRemoveAttachment
}: {
  onCancel?: () => void;
  onSend: (
    htmlBody: string,
    attachments: Attachment[],
    cc?: number[],
    bcc?: number[]
  ) => void;
  onMicClick?: () => void;
  onAttachFiles?: (files: Attachment[]) => Promise<void> | void;
  onSent?: () => void;
  placeholder?: string;
  accept?: string; 
  userOptions?: RecipientOption[]; 
  toUserId?: number;
  ccIds?:number[];
  bccIds?: number[];
  onRemoveAttachment?: (file: Attachment) => void;
}) {
  const { tokens: mTokens, isDark, primaryBtnSx, cancelBtnSx } = useModalStyles();
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [isVoicePopupOpen, setIsVoicePopupOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
 
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [ccRecipients, setCcRecipients] = useState<RecipientOption[]>([]);
  const [bccRecipients, setBccRecipients] = useState<RecipientOption[]>([]); 
  const dispatch = useAppDispatch();

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        bulletList: { keepMarks: true },
        orderedList: { keepMarks: true },
      }),
      Underline,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Placeholder.configure({ placeholder }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: { rel: "noreferrer noopener" },
      }),
    ],
    content: "",
    editorProps: {
      attributes: {
        style: [
          "min-height: 160px",
          "outline: none",
          "font-size: 0.95rem",
          "line-height: 1.35",
        ].join(";"),
      },
    },
  });

  const toBase64 = (f: File) =>
    new Promise<string>((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(String(r.result || "").split(",")[1] || "");
      r.onerror = reject;
      r.readAsDataURL(f);
    });

      const MAX_FILE_SIZE_MB = 50;
  const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

const handleFiles = async (files: FileList | null) => {
  if (!files || !files.length) return;

  const arr = Array.from(files);
  const validFiles: File[] = [];
  const oversizedFiles: string[] = [];

  arr.forEach((f) => {
    if (f.size > MAX_FILE_SIZE_BYTES) {
      oversizedFiles.push(f.name);
    } else {
      validFiles.push(f);
    }
  });

  if (validFiles.length > 0) {
    const built: Attachment[] = [];

    for (const f of validFiles) {
      built.push({
        tempId: `${f.name}-${Date.now()}-${Math.random()}`,
        documentName: f.name,
        type: "NOTIFICATION",
        content: await toBase64(f),
        fileType: f.type || "application/octet-stream",
      });
    }

    if (onAttachFiles) {
      await onAttachFiles(built);
    }
    setAttachments((prev) => [...prev, ...built]);
  }

  if (oversizedFiles.length > 0) {
    const alertObj: SingleAlertInfo = {
      message: `The following file(s) exceed the ${MAX_FILE_SIZE_MB}MB limit and were not attached: ${oversizedFiles.join(", ")}`,
      alertType: "error",
    };
    dispatch(setSingleAlertObj(alertObj));
  }
};

  const addLink = () => {
    if (!editor) return;
    const url = prompt("Enter URL:");
    if (!url) return;
    editor
      .chain()
      .focus()
      .extendMarkRange("link")
      .setLink({ href: url, target: "_blank" })
      .run();
  };

  const bgTop = mTokens.headerFooterBg;
  const border = mTokens.border;
  const editorBg = isDark ? mTokens.cardBg : mTokens.inputBg;
  const accentActive = mTokens.accent;

  const openFilePicker = () => fileInputRef.current?.click();
 
  const toIds = toUserId ? [toUserId] : [];
  const ccExcludeIds = [...toIds, ...bccRecipients.map((r) => r.value)];
  const bccExcludeIds = [...toIds, ...ccRecipients.map((r) => r.value)];

  const hasCcBccOptions = userOptions.length > 0;

  useEffect(() => {
    if (!userOptions.length) return;

    const mapRecipients = (ids: number[]) =>
      userOptions.filter((opt) => ids.includes(opt.value));
    if (ccIds?.length) {
      const ids = mapRecipients(ccIds)
      setCcRecipients(mapRecipients(ccIds));
      setShowCc(true);
    }

    if (bccIds?.length) {
      const ids = mapRecipients(bccIds)
      setBccRecipients(mapRecipients(bccIds));
      setShowBcc(true);
    }
  }, []);

  return (
    <Box
      sx={{
        p: 0,
        borderRadius: "12px",
        bgcolor: bgTop,
        border: `1px solid ${border}`,
        overflow: "hidden",
        boxShadow: isDark
          ? "0 8px 24px rgba(0,0,0,0.35)"
          : "0 4px 16px rgba(0,0,0,0.06)",
      }}
    > 
      {hasCcBccOptions && (
        <Box
          display="flex"
          justifyContent="flex-end"
          alignItems="center"
          gap={1}
          px={2}
          py={0.5}
          sx={{ borderBottom: `1px solid ${border}` }}
        >
          {!showCc && (
            <Typography
              variant="caption"
              onClick={() => setShowCc(true)}
              sx={{
                cursor: "pointer",
                fontWeight: "bold",
                color: mTokens.textSecondary,
                px: 0.75,
                py: 0.25,
                borderRadius: 1,
                "&:hover": {
                  color: mTokens.accent,
                  backgroundColor: isDark
                    ? "rgba(20,184,166,0.08)"
                    : "rgba(20,184,166,0.06)",
                },
              }}
            >
              Cc
            </Typography>
          )}
          {!showBcc && (
            <Typography
              variant="caption"
              onClick={() => setShowBcc(true)}
              sx={{
                cursor: "pointer",
                fontWeight: "bold",
                color: mTokens.textSecondary,
                px: 0.75,
                py: 0.25,
                borderRadius: 1,
                "&:hover": {
                  color: mTokens.accent,
                  backgroundColor: isDark
                    ? "rgba(20,184,166,0.08)"
                    : "rgba(20,184,166,0.06)",
                },
              }}
            >
              Bcc
            </Typography>
          )}
        </Box>
      )}
 
      {showCc && hasCcBccOptions && (
        <Box sx={{ bgcolor: bgTop }}>
          <CcBccField
            label="Cc"
            value={ccRecipients}
            onChange={setCcRecipients}
            options={userOptions}
            excludeIds={ccExcludeIds}
            onHide={() => setShowCc(false)}
          />
        </Box>
      )}
 
      {showBcc && hasCcBccOptions && (
        <Box sx={{ bgcolor: bgTop }}>
          <CcBccField
            label="Bcc"
            value={bccRecipients}
            onChange={setBccRecipients}
            options={userOptions}
            excludeIds={bccExcludeIds}
            onHide={() => setShowBcc(false)}
          />
        </Box>
      )}
 
      <Box
        sx={{
          px: 1.5,
          py: 0.75,
          display: "flex",
          alignItems: "center",
          gap: 0.25,
          borderBottom: `1px solid ${border}`,
        }}
      >
        <Tooltip title="Bold">
          <span>
            <IconButton
              size="small"
              onClick={() => editor?.chain().focus().toggleBold().run()}
              sx={{ color: editor?.isActive("bold") ? accentActive : "inherit" }}
            >
              <FormatBold />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Italic">
          <span>
            <IconButton
              size="small"
              onClick={() => editor?.chain().focus().toggleItalic().run()}
              sx={{ color: editor?.isActive("italic") ? accentActive : "inherit" }}
            >
              <FormatItalic />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Underline">
          <span>
            <IconButton
              size="small"
              onClick={() =>
                editor?.chain().focus().toggleUnderline().run()
              }
              sx={{ color: editor?.isActive("underline") ? accentActive : "inherit" }}
            >
              <FormatUnderlined />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Strikethrough">
          <span>
            <IconButton
              size="small"
              onClick={() => editor?.chain().focus().toggleStrike().run()}
              sx={{ color: editor?.isActive("strike") ? accentActive : "inherit" }}
            >
              <StrikethroughS />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Bulleted list">
          <span>
            <IconButton
              size="small"
              onClick={() =>
                editor?.chain().focus().toggleBulletList().run()
              }
              sx={{ color: editor?.isActive("bulletList") ? accentActive : "inherit" }}
            >
              <FormatListBulleted />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Numbered list">
          <span>
            <IconButton
              size="small"
              onClick={() =>
                editor?.chain().focus().toggleOrderedList().run()
              }
              sx={{ color: editor?.isActive("orderedList") ? accentActive : "inherit" }}
            >
              <FormatListNumbered />
            </IconButton>
          </span>
        </Tooltip>

        <Box sx={{ width: 8 }} />

        <Tooltip title="Align left">
          <span>
            <IconButton
              size="small"
              onClick={() =>
                editor?.chain().focus().setTextAlign("left").run()
              }
              sx={{
                color: editor?.isActive({ textAlign: "left" })
                  ? accentActive
                  : "inherit",
              }}
            >
              <FormatAlignLeft />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Align center">
          <span>
            <IconButton
              size="small"
              onClick={() =>
                editor?.chain().focus().setTextAlign("center").run()
              }
              sx={{
                color: editor?.isActive({ textAlign: "center" })
                  ? accentActive
                  : "inherit",
              }}
            >
              <FormatAlignCenter />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Align right">
          <span>
            <IconButton
              size="small"
              onClick={() =>
                editor?.chain().focus().setTextAlign("right").run()
              }
              sx={{
                color: editor?.isActive({ textAlign: "right" })
                  ? accentActive
                  : "inherit",
              }}
            >
              <FormatAlignRight />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Justify">
          <span>
            <IconButton
              size="small"
              onClick={() =>
                editor?.chain().focus().setTextAlign("justify").run()
              }
              sx={{
                color: editor?.isActive({ textAlign: "justify" })
                  ? accentActive
                  : "inherit",
              }}
            >
              <FormatAlignJustify />
            </IconButton>
          </span>
        </Tooltip>

        <Box sx={{ width: 8 }} />

        <Tooltip title="Insert link">
          <span>
            <IconButton size="small" onClick={addLink}>
              <InsertLink />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
 
      <Box
        sx={{
          px: 1.25,
          py: 1.25,
          bgcolor: editorBg,
          borderBottom: `1px solid ${border}`,
        }}
      >
        <Box
          sx={{
            borderRadius: "10px",
            border: `1px solid ${mTokens.inputBorder}`,
            bgcolor: editorBg,
            p: 1,
            minHeight: 180,
          }}
        >
          <EditorContent editor={editor} />
        </Box>

        {attachments.length > 0 && (
          <Box sx={{ mt: 1, display: "flex", gap: 1, flexWrap: "wrap" }}>
            {attachments.map((a, i) => (
              <Box
                key={`${a.documentName}-${i}`}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  px: 1,
                  py: 0.25,
                  borderRadius: 1.5,
                  bgcolor: isDark ? "rgba(255,255,255,0.06)" : mTokens.cardBg,
                  border: `1px solid ${mTokens.border}`,
                  fontSize: 12,
                  color: mTokens.textPrimary,
                }}
              >
                <Typography variant="caption">{a.documentName}</Typography>
                <IconButton
                  size="small"
                  onClick={() =>
                  {
                    onRemoveAttachment?.(a); 
                    setAttachments((prev) =>
                      prev.filter((_, idx) => idx !== i)
                    )
                  }
                  }
                >
                  <CloseOutlined fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Box>
        )}
      </Box>
 
      <Box
        sx={{
          px: 1.25,
          py: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          bgcolor: bgTop,
          borderTop: `1px solid ${border}`,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
          <IconButton
            sx={{ color: mTokens.textSecondary, "&:hover": { color: mTokens.textPrimary } }}
            onClick={openFilePicker}
          >
            <AttachFile />
          </IconButton>
          <input
            ref={fileInputRef}
            type="file"
            hidden
            multiple
            accept={accept}
            onChange={(e) => handleFiles(e.target.files)}
          />

          <Tooltip title="Speech to text" placement="top" arrow>
            <IconButton
              color="default"
              component="label"
              sx={{
                p: 1,
                ml: 1,
                borderRadius: 2,
                bgcolor: mTokens.accent,
                color: "#fff",
                transition: "background-color 0.4s ease",
                "&:hover": { bgcolor: mTokens.accentHover },
              }}
              onClick={() => {
                setIsVoicePopupOpen(true);
                onMicClick?.();
              }}
            >
              <KeyboardVoice />
            </IconButton>
          </Tooltip>
        </Box>

        <Box display="flex" alignItems="center" gap={1}>
          <Button
            variant="outlined"
            color="error"
            onClick={() => {
              editor?.commands.clearContent();
              setAttachments([]);
              setCcRecipients([]);
              setBccRecipients([]);
              setShowCc(false);
              setShowBcc(false);
              onCancel?.();
            }}
            sx={{
              ...cancelBtnSx,
              mr: 1,
            }}
          >
            Close
          </Button>

          <Button
            variant="contained"
            endIcon={<Send />}
            onClick={() => {
              const html = editor?.getHTML() || "";
              const plain = html.replace(/<[^>]*>/g, "").trim();
              if (!plain && attachments.length === 0) return;
              onSend(
                html,
                attachments,
                ccRecipients.map((r) => r.value),
                bccRecipients.map((r) => r.value)
              );
              onSent?.();
              editor?.commands.clearContent();
              setAttachments([]);
              setCcRecipients([]);
              setBccRecipients([]);
              setShowCc(false);
              setShowBcc(false);
            }}
            sx={{
              ...primaryBtnSx,
              color: "#fff",
              px: 2,
              "& .MuiButton-endIcon": { color: "#fff" },
            }}
          >
            Send
          </Button>
        </Box>
      </Box>

      <SpeechToTextPopup
        open={isVoicePopupOpen}
        onClose={() => setIsVoicePopupOpen(false)}
        onSubmitVoice={(text: string) => {
          if (editor) {
            editor.commands.insertContent(text);
          }
        }}
      />
    </Box>
  );
}